import { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { collection, doc, getDoc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { AuthProvider, useAuth } from '../firebase/useAuth';
import { db } from '../firebase/firebaseConfig';
import { initialState, reducer } from './reducer';
import { CATEGORIES } from '../data/categories';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  return (
    <AuthProvider>
      <AppProviderInner>{children}</AppProviderInner>
    </AuthProvider>
  );
}

function AppProviderInner({ children }) {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);
  const friendListeners = useRef({});

  // Fetch daily prompts once on mount
  useEffect(() => {
    getDoc(doc(db, 'config', 'dailyPrompts')).then((snap) => {
      if (snap.exists()) {
        dispatch({ type: 'SET_PROMPTS', prompts: snap.data() });
      }
    }).catch(err => console.warn('Failed to load prompts:', err));
  }, []);

  // Subscribe to current user's entries when authenticated
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'users', user.uid, 'entries'),
      orderBy('date', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const entries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      dispatch({ type: 'SET_ENTRIES', entries });
    });
    return () => unsub();
  }, [user]);

  // Subscribe to friends list + dynamically manage per-friend entry listeners
  useEffect(() => {
    if (!user) return;

    const unsubFriends = onSnapshot(
      collection(db, 'access', user.uid, 'viewers'),
      (snap) => {
        const friends = snap.docs.map(d => ({
          userId: d.id,
          displayName: d.data().displayName || 'Unknown',
          photoURL: d.data().photoURL || null,
          addedAt: d.data().addedAt || null,
        }));
        dispatch({ type: 'SET_FRIENDS', friends });

        const activeFriendIds = new Set(friends.map(f => f.userId));

        // Tear down listeners for removed friends
        Object.keys(friendListeners.current).forEach((fid) => {
          if (!activeFriendIds.has(fid)) {
            friendListeners.current[fid]();
            delete friendListeners.current[fid];
            dispatch({ type: 'REMOVE_FRIEND_ENTRIES', friendId: fid });
          }
        });

        // Add listeners for newly added friends
        friends.forEach((friend) => {
          if (!friendListeners.current[friend.userId]) {
            const q = query(
              collection(db, 'users', friend.userId, 'entries'),
              orderBy('date', 'desc')
            );
            friendListeners.current[friend.userId] = onSnapshot(
              q,
              (entrySnap) => {
                const entries = entrySnap.docs
                  .map(d => ({ id: d.id, ...d.data() }))
                  // friends see fully-submitted entries OR entries with at least one individually-submitted category
                  .filter(e => !e.isDraft || CATEGORIES.some(c => e[c.key]?.submitted));
                dispatch({ type: 'SET_FRIEND_ENTRIES', friendId: friend.userId, entries });
              },
              (err) => console.warn(`Friend entries listener error (${friend.userId}):`, err)
            );
          }
        });
      },
      (err) => console.warn('Friends listener error:', err)
    );

    return () => {
      unsubFriends();
      Object.values(friendListeners.current).forEach(u => u());
      friendListeners.current = {};
    };
  }, [user]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
